// Η κατάσταση περιήγησης ζει στη **διεύθυνση**, όχι σε `useState`.
//
// Αναζήτηση, φίλτρα, ταξινόμηση, σελίδα και ο ανοιχτός πόρος: όλα γράφονται στη
// συμβολοσειρά ερωτήματος. Δύο λόγοι, και ο δεύτερος είναι απαίτηση.
//
//   1. **Μοιράζεται.** Ένα αποτέλεσμα αναζήτησης που δεν μπορείς να στείλεις σε
//      συνάδελφο είναι μισό. Το §2.1 ορίζει την ταυτοποίηση ψηφιακού
//      αντικειμένου ως **μόνιμη και επιλύσιμη αναφορά**, και η ΛΑ-14 ζητά
//      εντοπισμό **και ανάκτηση**.
//   2. **Επιβιώνει σε ανανέωση.** Κατάσταση σε μνήμη χάνεται με το F5, και ο
//      χρήστης ξαναρχίζει το φιλτράρισμα από την αρχή.
//
// Καθαρές συναρτήσεις πάνω σε `URLSearchParams`, δοκιμασμένες στο
// `browseState.test.js`. Ο λόγος είναι ο ίδιος που ο κύκλος επεξεργασίας πόρου
// ζει στο `resourceEdit.js`: η λογική που δοκιμάζεται δεν χρειάζεται να
// επιθεωρείται με το μάτι, και οι μεταβάσεις της διεύθυνσης είναι ακριβώς το
// είδος που σπάει σιωπηλά.

/** Οι προεπιλογές. Ό,τι ισούται με αυτές **δεν γράφεται** στη διεύθυνση. */
export const DEFAULTS = Object.freeze({
  q: '',
  page: 1,
  sort: 'createdAt',
  order: 'desc',
  tags: [],
  createdBy: '',
  from: '',
  to: '',
  agency: '',
  field: '',
  deadlineBefore: '',
  minAmount: '',
  eligible: '',
  relevance: '',
  // **Απόφαση D27.** Η ενεργή καρτέλα είναι μία ακόμη παράμετρος της ίδιας
  // κατάστασης, όχι δεύτερος μηχανισμός: έτσι ο μόνιμος σύνδεσμος πόρου
  // εξακολουθεί να δουλεύει και μοιράζεται μαζί με την καρτέλα.
  tab: 'resources'
});

/** Οι επιτρεπτές ταξινομήσεις ανά οθόνη, ίδιες με το `api/lib/searchQuery.js`. */
export const SORTS = Object.freeze({
  resources: Object.freeze(['createdAt', 'updatedAt', 'title']),
  collections: Object.freeze(['updatedAt', 'createdAt', 'title'])
});

/**
 * Οι καρτέλες της σελίδας συλλογής (Σετ Β, **Απόφαση D27**).
 *
 * Η **πρώτη** είναι η προεπιλογή: ό,τι ισούται με αυτήν δεν γράφεται στη
 * διεύθυνση, όπως ακριβώς και η πρώτη ταξινόμηση.
 *
 * Ζει εδώ και όχι μέσα στη σελίδα, γιατί την καταναλώνουν **τρεις**: η ανάγνωση
 * της διεύθυνσης, η εγγραφή της, και η `resourceDialogHost` παρακάτω. Τρία
 * αντίγραφα ενός καταλόγου σε ένα αρχείο είναι το ίδιο μοτίβο με τρία σε τρία
 * πακέτα — απλώς φθηνότερο να διορθωθεί σήμερα.
 */
export const TABS = Object.freeze({
  collection: Object.freeze(['resources', 'tasks', 'judgements', 'activity', 'members'])
});

/**
 * Διαβάζει την κατάσταση από τη διεύθυνση.
 *
 * Κάθε τιμή **επικυρώνεται**: άγνωστη ταξινόμηση ή αρνητική σελίδα πέφτουν στην
 * προεπιλογή αντί να σταλούν στο API. Ο διακομιστής θα τις απέρριπτε με 422,
 * και ο χρήστης θα έβλεπε σφάλμα για διεύθυνση που πιθανότατα κόπηκε στη
 * μεταφορά — όχι για κάτι που έκανε.
 */
export function readBrowseState(search, { sorts = SORTS.resources, tabs = null } = {}) {
  const params = new URLSearchParams(search);

  const page = Number.parseInt(params.get('page') ?? '', 10);
  const sort = params.get('sort') ?? '';
  const order = params.get('order') ?? '';
  const tab = params.get('tab') ?? '';
  const tags = (params.get('tags') ?? '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

  return {
    q: params.get('q') ?? DEFAULTS.q,
    page: Number.isInteger(page) && page > 0 ? page : DEFAULTS.page,
    sort: sorts.includes(sort) ? sort : sorts[0],
    order: order === 'asc' ? 'asc' : 'desc',
    tags,
    createdBy: params.get('createdBy') ?? DEFAULTS.createdBy,
    from: params.get('from') ?? DEFAULTS.from,
    to: params.get('to') ?? DEFAULTS.to,
    agency: params.get('agency') ?? DEFAULTS.agency,
    field: params.get('field') ?? DEFAULTS.field,
    deadlineBefore: params.get('deadlineBefore') ?? DEFAULTS.deadlineBefore,
    minAmount: params.get('minAmount') ?? DEFAULTS.minAmount,
    eligible: params.get('eligible') ?? DEFAULTS.eligible,
    relevance: params.get('relevance') ?? DEFAULTS.relevance,
    // feat/topics — **D47** (ΛΑ-06): «προβολή των συλλογών ανά θεματικό
    // αντικείμενο». Δεν επικυρώνεται έναντι καταλόγου, σε αντίθεση με την
    // ταξινόμηση: άγνωστο θέμα δίνει **κενό αποτέλεσμα**, που είναι έγκυρη
    // απάντηση, και ο πελάτης δεν έχει το λεξιλόγιο τη στιγμή που διαβάζει τη
    // διεύθυνση.
    topic: params.get('topic') ?? '',
    // Ο ανοιχτός πόρος είναι μέρος της ίδιας κατάστασης και όχι χωριστός
    // μηχανισμός: ο συγχρονισμός διεύθυνσης και οθόνης γράφεται μία φορά.
    resource: params.get('resource') ?? '',
    // **Ίδιο πρότυπο με την ταξινόμηση**: άγνωστη τιμή πέφτει στην προεπιλογή
    // αντί να ταξιδέψει. Οθόνη **χωρίς** καρτέλες δεν παίρνει `tabs`, οπότε το
    // πεδίο μένει κενό και δεν γράφεται ποτέ — ο κατάλογος συλλογών και η
    // αναζήτηση δεν επηρεάζονται.
    tab: tabs ? (tabs.includes(tab) ? tab : tabs[0]) : ''
  };
}

/**
 * Γράφει την κατάσταση στη διεύθυνση, **παραλείποντας τις προεπιλογές**.
 *
 * Χωρίς την παράλειψη, η καθαρή σελίδα θα είχε διεύθυνση
 * `?q=&page=1&sort=createdAt&order=desc&tags=` — θόρυβος που κάνει τον σύνδεσμο
 * αντιπαθητικό να μοιραστεί και κρύβει ποια φίλτρα είναι όντως ενεργά.
 */
export function writeBrowseState(state, { sorts = SORTS.resources, tabs = null } = {}) {
  const params = new URLSearchParams();
  const defaultSort = sorts[0];

  if (state.q) params.set('q', state.q);
  if (state.tags?.length) params.set('tags', state.tags.join(','));
  if (state.createdBy) params.set('createdBy', state.createdBy);
  if (state.from) params.set('from', state.from);
  if (state.to) params.set('to', state.to);
  if (state.agency) params.set('agency', state.agency);
  if (state.field) params.set('field', state.field);
  if (state.deadlineBefore) params.set('deadlineBefore', state.deadlineBefore);
  if (state.minAmount) params.set('minAmount', state.minAmount);
  if (state.eligible) params.set('eligible', state.eligible);
  if (state.relevance) params.set('relevance', state.relevance);
  if (state.sort && state.sort !== defaultSort) params.set('sort', state.sort);
  if (state.order && state.order !== DEFAULTS.order) params.set('order', state.order);
  if (state.page && state.page > 1) params.set('page', String(state.page));
  if (state.resource) params.set('resource', state.resource);
  // Η προεπιλεγμένη καρτέλα δεν γράφεται, όπως δεν γράφεται η προεπιλεγμένη
  // ταξινόμηση: αλλιώς κάθε σύνδεσμος συλλογής θα κουβαλούσε `tab=resources`,
  // δηλαδή θόρυβο που κρύβει ποια καρτέλα είναι όντως επιλεγμένη.
  if (tabs && state.tab && state.tab !== tabs[0]) params.set('tab', state.tab);

  return params.toString();
}

/**
 * Η μεταβολή ενός φίλτρου **μηδενίζει τη σελίδα**.
 *
 * Χωρίς αυτό, ο χρήστης που βρίσκεται στη σελίδα 4 και πληκτρολογεί όρο
 * αναζήτησης βλέπει κενό αποτέλεσμα — γιατί το φιλτραρισμένο σύνολο έχει μία
 * σελίδα — και συμπεραίνει ότι δεν υπάρχει τίποτα. Είναι ψευδής ισχυρισμός
 * παραγόμενος από τη διεπαφή και όχι από τα δεδομένα (ΜΛΑ-03).
 */
export function withFilter(state, changes) {
  return { ...state, ...changes, page: 1 };
}

/** Το ερώτημα που στέλνεται στο API. Ό,τι είναι προεπιλογή δεν ταξιδεύει. */
export function toApiQuery(state) {
  const params = new URLSearchParams();

  if (state.q) params.set('q', state.q);
  if (state.tags?.length) params.set('tags', state.tags.join(','));
  if (state.createdBy) params.set('createdBy', state.createdBy);
  if (state.from) params.set('from', state.from);
  if (state.to) params.set('to', state.to);
  if (state.agency) params.set('agency', state.agency);
  if (state.field) params.set('field', state.field);
  if (state.deadlineBefore) params.set('deadlineBefore', state.deadlineBefore);
  if (state.minAmount) params.set('minAmount', state.minAmount);
  if (state.eligible) params.set('eligible', state.eligible);
  if (state.relevance) params.set('relevance', state.relevance);
  if (state.collection) params.set('collection', state.collection);
  // feat/topics — **D47** (ΛΑ-06). ⚠ Χωρίς αυτή τη γραμμή το φίλτρο θέματος
  // υπήρχε σε **τρία** σημεία —διαβαζόταν από τη διεύθυνση, μετρούσε στο
  // «φιλτράρει κάτι;», και ονομαζόταν στην οθόνη— και **έλειπε από το ένα που
  // το βάζει στο σύρμα**. Ο σύνδεσμος άλλαζε τη διεύθυνση και ο κατάλογος
  // έμενε ολόκληρος: *αστοχία που μοιάζει με λειτουργία, γιατί όλα τα ορατά
  // σημάδια του φίλτρου είναι παρόντα εκτός από το αποτέλεσμά του.*
  if (state.topic) params.set('topic', state.topic);
  if (state.sort) params.set('sort', state.sort);
  if (state.order) params.set('order', state.order);
  if (state.page > 1) params.set('page', String(state.page));

  return params.toString();
}

/** Αληθές όταν κάτι φιλτράρει πράγματι — ορίζει το μήνυμα κενού αποτελέσματος. */
export function isFiltering(state) {
  return Boolean(
    state.q || state.tags?.length || state.createdBy || state.from || state.to ||
    state.agency || state.field || state.deadlineBefore || state.minAmount ||
    // feat/topics — **D47**: το θέμα φιλτράρει, άρα το μήνυμα κενού αποτελέσματος
    // πρέπει να λέει «καμία αντιστοιχία» και όχι «δεν υπάρχουν συλλογές».
    state.eligible || state.relevance || state.topic
  );
}

/**
 * Τα **δομημένα** φίλτρα — δηλαδή όλα εκτός από το ελεύθερο κείμενο (D30).
 *
 * **Γιατί δεν είναι το `isFiltering` από πάνω.** Οι δύο συναρτήσεις έχουν σχεδόν
 * το ίδιο σώμα και **διαφορετικό σκοπό**, και η μία δεν είναι υποκατάστατο της
 * άλλης. Το `isFiltering` απαντά «να πω *καμία αντιστοιχία* ή *κανένας πόρος*;»,
 * οπότε ο όρος αναζήτησης **μετράει**. Αυτή εδώ απαντά «να ανοίξει η περιοχή των
 * φίλτρων;», και ο όρος αναζήτησης **δεν** μετράει: το πεδίο του είναι πάντοτε
 * ορατό, οπότε σύνδεσμος με μόνο `q` δεν έχει τίποτα κρυμμένο να αποκαλύψει.
 *
 * Δύο σκοποί που συμπίπτουν στα εννέα δέκατα και αποκλίνουν στο ένα είναι
 * ακριβώς η περίπτωση όπου η επαναχρήση μεταφέρει τη **μορφή** και όχι τον
 * **λόγο**.
 */
export function hasStructuredFilters(state) {
  return Boolean(
    state.tags?.length || state.createdBy || state.from || state.to ||
    state.agency || state.field || state.deadlineBefore || state.minAmount ||
    state.eligible || state.relevance
  );
}

/**
 * **Ποιος αποδίδει τον διάλογο του πόρου** — μία απάντηση, δύο καταναλωτές.
 *
 * Με τις καρτέλες, ο διάλογος έχει **δύο πιθανούς οικοδεσπότες**: την κάρτα μέσα
 * στη λίστα των πόρων, και μια θέση της σελίδας έξω από τα πάνελ. Και οι δύο
 * τρόποι αποτυχίας είναι **σιωπηλοί**: κανένας οικοδεσπότης σημαίνει σύνδεσμος
 * που δεν ανοίγει τίποτα, δύο οικοδεσπότες σημαίνουν δύο διαλόγους ο ένας πάνω
 * στον άλλον. Κανένα από τα δύο δεν πετά σφάλμα.
 *
 * Γι' αυτό η απόφαση δεν γράφεται δύο φορές ως συνθήκη απόδοσης. Γράφεται **μία**,
 * εδώ, και τα δύο σημεία ρωτούν αυτήν: το κενό ανάμεσά τους δεν ελέγχεται —
 * **καταργείται**.
 *
 * @returns {'list'|'page'|'none'}
 */
export function resourceDialogHost(state, { tabs = TABS.collection } = {}) {
  if (!state.resource) return 'none';
  return state.tab === tabs[0] ? 'list' : 'page';
}

/**
 * **Ποιος αποδίδει τον διάλογο ΚΑΙ ΜΕ ΠΟΙΟΝ ΠΟΡΟ** — η ολόκληρη απόφαση, όχι η μισή.
 *
 * ⚠ **Γεννήθηκε από σφάλμα που η προηγούμενη μορφή δεν μπορούσε να εκφράσει.** Η
 * `resourceDialogHost` απαντούσε σωστά `'page'`, και η σελίδα δεν άνοιγε τίποτα:
 * το αντικείμενο του πόρου το κρατούσε μια μεταβλητή γραμμένη για **άλλο ερώτημα**
 * —«λείπει ο πόρος από τη φορτωμένη σελίδα;»— που μηδενιζόταν ακριβώς όταν ο πόρος
 * **υπήρχε** εκεί. Δηλαδή ο οικοδεσπότης της σελίδας έμενε χωρίς τι να δείξει στη
 * **συνηθισμένη** περίπτωση, και δούλευε μόνο στη σπάνια.
 *
 * Η αιτία δεν ήταν η συνάρτηση αλλά ο **διαχωρισμός**: η μία μισή απόφαση εδώ, η
 * άλλη μισή —ποιο αντικείμενο— στη σελίδα. Δύο μισά σε δύο αρχεία δεν έχουν
 * συμβόλαιο, και καμία δοκιμή καθαρής συνάρτησης δεν μπορεί να δει τον καλούντα
 * της. Η απόφαση επιστρέφει πλέον **και τα δύο μαζί**, ώστε να μην υπάρχει κενό
 * ανάμεσά τους.
 *
 * @returns {{host: 'list'|'page'|'none', resource: object|null}}
 */
export function resourceDialogPlan(state, { resources = [], linked = null } = {}, options = {}) {
  const host = resourceDialogHost(state, options);
  if (host === 'none') return { host, resource: null };

  // Ο πόρος της φορτωμένης σελίδας **προηγείται**: είναι το φρέσκο αντικείμενο,
  // αυτό που μόλις ανανέωσε η ανάγνωση της συλλογής. Το `linked` είναι η εφεδρεία
  // για τον πόρο που ζει σε **άλλη** σελίδα και ζητήθηκε ονομαστικά.
  const fromPage = resources.find((item) => String(item.id) === String(state.resource)) ?? null;

  return { host, resource: fromPage ?? linked ?? null };
}

// --- Μόνιμος σύνδεσμος πόρου ---------------------------------------------------

/**
 * Η διεύθυνση με ανοιχτό τον πόρο.
 *
 * **Απόφαση D15.** Το άνοιγμα προσθέτει καταχώρηση ιστορικού και το κλείσιμο
 * την αναιρεί με `navigate(-1)`. Το changelog ζητούσε αντικατάσταση, με σκοπό
 * να μη γεμίζει το ιστορικό από δέκα ανοιγοκλεισίματα· ζητούσε όμως και να
 * **κλείνει το modal το κουμπί επιστροφής**, που με αντικατάσταση είναι
 * αδύνατο — δεν υπάρχει καταχώρηση να αναιρεθεί και το «πίσω» φεύγει από τη
 * σελίδα. Με προσθήκη και αναίρεση, ο κύκλος ανοίγω-κλείνω αφήνει το ιστορικό
 * **αμετάβλητο**, δηλαδή ο σκοπός του αρχικού κανόνα τηρείται ακριβώς.
 */
export function withResource(search, resourceId) {
  const params = new URLSearchParams(search);
  params.set('resource', resourceId);
  return params.toString();
}

/** Η ίδια διεύθυνση χωρίς τον πόρο: για το κλείσιμο όταν δεν υπάρχει ιστορικό. */
export function withoutResource(search) {
  const params = new URLSearchParams(search);
  params.delete('resource');
  return params.toString();
}

/**
 * Δείχνει η διεύθυνση σε **αυτόν** τον πόρο;
 *
 * Αντικαθιστά το παλιό `hashPointsTo`. Το `#<id>` ταίριαζε μόνο με πόρο που
 * βρισκόταν ήδη στη **φορτωμένη σελίδα**: με τη σελιδοποίηση στον διακομιστή,
 * σύνδεσμος προς πόρο της τρίτης σελίδας δεν άνοιγε τίποτα, γιατί το άγκιστρο
 * δεν φτάνει ποτέ στον εξυπηρετητή. Η παράμετρος φτάνει.
 */
export function pointsToResource(search, resourceId) {
  if (!resourceId) return false;
  return new URLSearchParams(search).get('resource') === String(resourceId);
}

export default {
  DEFAULTS,
  SORTS,
  TABS,
  readBrowseState,
  writeBrowseState,
  withFilter,
  toApiQuery,
  isFiltering,
  hasStructuredFilters,
  resourceDialogHost,
  resourceDialogPlan,
  withResource,
  withoutResource,
  pointsToResource
};
